import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { apiErr, fileUrl } from "@/lib/api";
import { formatNumber } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { Plus, Search, Upload, FileCode, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Designs() {
  const { user } = useAuth();
  const [designs, setDesigns] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", stitch_count: 0, width_cm: 0, height_cm: 0, colors: "", file_type: "", notes: "", preview_url: "" });
  const [designFileUrl, setDesignFileUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const previewRef = useRef();
  const fileRef = useRef();

  const load = useCallback(() => {
    api.get("/designs", { params: q ? { q } : {} }).then((r) => setDesigns(r.data)).catch(() => {});
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const canEdit = user?.role === "admin" || user?.role === "designer";

  const upload = async (file, kind) => {
    if (!file) return null;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post(`/upload?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${kind === "preview" ? "Preview" : "File desain"} terunggah`);
      return data;
    } catch (e) { toast.error(apiErr(e)); return null; }
    finally { setUploading(false); }
  };

  const onPreview = async (e) => {
    const data = await upload(e.target.files[0], "preview");
    if (data) setForm((f) => ({ ...f, preview_url: fileUrl(data.storage_path) }));
  };
  const onDesignFile = async (e) => {
    const f = e.target.files[0];
    const data = await upload(f, "design");
    if (data) { setDesignFileUrl(fileUrl(data.storage_path)); setForm((p) => ({ ...p, file_type: (f.name.split(".").pop() || "").toUpperCase() })); }
  };

  const save = async () => {
    if (!form.name) { toast.error("Nama desain wajib diisi"); return; }
    const payload = {
      ...form,
      stitch_count: +form.stitch_count, width_cm: +form.width_cm, height_cm: +form.height_cm,
      colors: form.colors.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try { await api.post("/designs", payload); toast.success("Desain disimpan"); setOpen(false);
      setForm({ name: "", stitch_count: 0, width_cm: 0, height_cm: 0, colors: "", file_type: "", notes: "", preview_url: "" });
      setDesignFileUrl(""); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 md:text-3xl">Katalog Desain</h1>
          <p className="text-sm text-slate-500">Pustaka aset bordir: file .DST/.PES/.EMB, stitch count, dan preview.</p>
        </div>
        {canEdit && <Button data-testid="btn-add-design" className="bg-blue-600 hover:bg-blue-700" onClick={() => setOpen(true)}><Plus className="mr-2 h-4 w-4" /> Tambah Desain</Button>}
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input data-testid="input-design-search" className="pl-9" placeholder="Cari nama desain..." value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {designs.map((d) => (
          <Card key={d.id} data-testid={`design-card-${d.id}`} className="overflow-hidden border-slate-200/80 shadow-sm hover:shadow-md transition-shadow">
            <div className="aspect-square bg-slate-100 overflow-hidden">
              {d.preview_url ? <img src={d.preview_url} alt={d.name} className="h-full w-full object-cover" /> :
                <div className="flex h-full items-center justify-center text-slate-300"><FileCode className="h-12 w-12" /></div>}
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-slate-900 truncate">{d.name}</p>
                {d.file_type && <span className="rounded bg-slate-900 px-1.5 py-0.5 text-[10px] font-bold text-white">{d.file_type}</span>}
              </div>
              <p className="mt-1 text-xs text-slate-500">{formatNumber(d.stitch_count)} stitch · {d.width_cm}×{d.height_cm} cm</p>
              <div className="mt-2 flex flex-wrap gap-1">
                {(d.colors || []).slice(0, 4).map((c, i) => <span key={i} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600">{c}</span>)}
              </div>
            </div>
          </Card>
        ))}
        {designs.length === 0 && <p className="text-slate-400 col-span-full text-center py-10">Belum ada desain.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Tambah Desain</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Nama Desain *</Label><Input data-testid="design-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Stitch</Label><Input type="number" data-testid="design-stitch" value={form.stitch_count} onChange={(e) => setForm({ ...form, stitch_count: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Lebar (cm)</Label><Input type="number" value={form.width_cm} onChange={(e) => setForm({ ...form, width_cm: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Tinggi (cm)</Label><Input type="number" value={form.height_cm} onChange={(e) => setForm({ ...form, height_cm: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Warna Benang (koma)</Label><Input data-testid="design-colors" value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} placeholder="Navy RY-1134, Gold MT-GOLD" /></div>
            <div className="space-y-1.5"><Label>Catatan</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>File Desain (.DST/.PES/.EMB)</Label>
                <input ref={fileRef} type="file" accept=".dst,.pes,.emb" className="hidden" onChange={onDesignFile} />
                <Button data-testid="btn-upload-design-file" type="button" variant="outline" className="mt-1.5 w-full" onClick={() => fileRef.current?.click()} disabled={uploading}>
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="mr-2 h-4 w-4" /> {designFileUrl ? "Terunggah ✓" : "Upload File"}</>}
                </Button>
              </div>
              <div>
                <Label>Gambar Preview</Label>
                <input ref={previewRef} type="file" accept="image/*" className="hidden" onChange={onPreview} />
                <Button data-testid="btn-upload-preview" type="button" variant="outline" className="mt-1.5 w-full" onClick={() => previewRef.current?.click()} disabled={uploading}>
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="mr-2 h-4 w-4" /> {form.preview_url ? "Terunggah ✓" : "Upload Gambar"}</>}
                </Button>
              </div>
            </div>
            {form.preview_url && <img src={form.preview_url} alt="preview" className="h-32 w-32 rounded-lg object-cover border" />}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button data-testid="btn-save-design" className="bg-blue-600 hover:bg-blue-700" onClick={save}>Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
